import React from "react";
import { FaCheck, FaTimes, FaHourglassHalf, FaUserShield, FaHome, FaUser, FaKey } from "react-icons/fa";

/**
 * Displays the 4-stage vacate workflow as a vertical timeline.
 * Stages: Tenant Submission → Admin Review → Owner Approval → Admin Vacate Confirmation
 */
export default function VacateWorkflowTimeline({ request }) {
  if (!request) return null;

  const status = request.STATUS || "pending";

  const fmt = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  // Whether owner approved (to distinguish pending_vacate_confirmation vs owner_rejected in stage 3)
  const ownerApprovedStatuses = ["pending_vacate_confirmation", "vacate_confirmed"];

  // ── Derive per-stage state ─────────────────────────────────────────────────
  const stages = [
    {
      key: "tenant",
      icon: FaUser,
      title: "Tenant Submission",
      actor: request.TENANT_NAME || request.TENANT_EMAIL,
      date: fmt(request.CREATED_AT),
      notes: request.REASON,
      state: "done", // always done if record exists
      badge: { bg: "bg-green-100", text: "text-green-800", label: "Submitted" },
    },
    {
      key: "admin_initial",
      icon: FaUserShield,
      title: "Admin Review",
      actor: request.ADMIN_REVIEWED_BY || null,
      date: fmt(request.ADMIN_REVIEWED_AT),
      notes: request.ADMIN_REVIEW_NOTES || request.ADMIN_REJECT_REASON,
      societyDues: request.SOCIETY_DUES_STATUS,
      state: (() => {
        if (status === "admin_rejected") return "rejected";
        if (["admin_approved", "owner_review", "pending_vacate_confirmation", "owner_approved", "owner_rejected", "vacate_confirmed"].includes(status)) return "approved";
        if (status === "admin_review") return "in_progress";
        return "pending";
      })(),
      badge: (() => {
        if (status === "admin_rejected") return { bg: "bg-red-100", text: "text-red-800", label: "Rejected" };
        if (["admin_approved", "owner_review", "pending_vacate_confirmation", "owner_approved", "owner_rejected", "vacate_confirmed"].includes(status))
          return { bg: "bg-green-100", text: "text-green-800", label: "Approved" };
        if (status === "admin_review") return { bg: "bg-purple-100", text: "text-purple-800", label: "In Review" };
        return { bg: "bg-gray-100", text: "text-gray-500", label: "Pending" };
      })(),
    },
    {
      key: "owner",
      icon: FaHome,
      title: "Owner Approval",
      actor: request.OWNER_REVIEWED_BY || null,
      date: fmt(request.OWNER_REVIEWED_AT),
      notes: request.OWNER_REVIEW_NOTES || request.OWNER_REJECT_REASON,
      state: (() => {
        if (status === "owner_rejected") return "rejected";
        if (ownerApprovedStatuses.includes(status)) return "approved";
        if (["admin_approved", "owner_review"].includes(status)) return "in_progress";
        return "pending";
      })(),
      badge: (() => {
        if (status === "owner_rejected") return { bg: "bg-red-100", text: "text-red-800", label: "Rejected" };
        if (ownerApprovedStatuses.includes(status)) return { bg: "bg-teal-100", text: "text-teal-800", label: "Approved" };
        if (["admin_approved", "owner_review"].includes(status))
          return { bg: "bg-blue-100", text: "text-blue-800", label: "Awaiting Review" };
        return { bg: "bg-gray-100", text: "text-gray-400", label: "Not Started" };
      })(),
    },
    {
      key: "admin_confirm",
      icon: FaKey,
      title: "Admin Vacate Confirmation",
      actor: request.VACATE_CONFIRMED_BY || null,
      date: fmt(request.VACATE_CONFIRMED_AT),
      notes: request.VACATE_CONFIRMED_NOTES || null,
      state: (() => {
        if (status === "vacate_confirmed") return "approved";
        if (status === "pending_vacate_confirmation") return "in_progress";
        return "pending";
      })(),
      badge: (() => {
        if (status === "vacate_confirmed") return { bg: "bg-green-100", text: "text-green-800", label: "Vacate Confirmed ✓" };
        if (status === "pending_vacate_confirmation") return { bg: "bg-orange-100", text: "text-orange-800", label: "Awaiting Confirmation" };
        return { bg: "bg-gray-100", text: "text-gray-400", label: "Not Started" };
      })(),
    },
  ];

  const stateIcon = (state) => {
    if (state === "approved") return <FaCheck className="text-green-600" size={12} />;
    if (state === "rejected") return <FaTimes className="text-red-600" size={12} />;
    if (state === "in_progress") return <FaHourglassHalf className="text-blue-500" size={12} />;
    return <div className="w-3 h-3 rounded-full border-2 border-gray-300" />;
  };

  const connectorColor = (state) => {
    if (state === "approved") return "bg-green-400";
    if (state === "rejected") return "bg-red-400";
    return "bg-gray-200";
  };

  return (
    <div className="space-y-0">
      {stages.map((stage, idx) => {
        const Icon = stage.icon;
        const isLast = idx === stages.length - 1;
        return (
          <div key={stage.key} className="flex gap-3">
            {/* Left spine */}
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  stage.state === "approved"
                    ? "bg-green-100"
                    : stage.state === "rejected"
                    ? "bg-red-100"
                    : stage.state === "in_progress"
                    ? "bg-blue-100"
                    : stage.state === "done"
                    ? "bg-green-100"
                    : "bg-gray-100"
                }`}
              >
                <Icon
                  size={14}
                  className={
                    stage.state === "approved" || stage.state === "done"
                      ? "text-green-600"
                      : stage.state === "rejected"
                      ? "text-red-600"
                      : stage.state === "in_progress"
                      ? "text-blue-600"
                      : "text-gray-400"
                  }
                />
              </div>
              {!isLast && (
                <div className={`w-0.5 flex-1 min-h-4 my-1 ${connectorColor(stage.state)}`} />
              )}
            </div>

            {/* Content */}
            <div className={`pb-4 flex-1 ${isLast ? "" : ""}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-gray-800">{stage.title}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stage.badge.bg} ${stage.badge.text}`}>
                  {stage.badge.label}
                </span>
                {stateIcon(stage.state)}
              </div>

              {stage.actor && (
                <p className="text-xs text-gray-500">
                  <span className="font-medium">By:</span> {stage.actor}
                </p>
              )}
              {stage.date && (
                <p className="text-xs text-gray-500">
                  <span className="font-medium">On:</span> {stage.date}
                </p>
              )}
              {stage.societyDues && stage.state === "approved" && (
                <p className="text-xs text-gray-600 mt-0.5">
                  <span className="font-medium">Society Dues:</span>{" "}
                  <span
                    className={
                      stage.societyDues === "clear"
                        ? "text-green-700 font-semibold"
                        : "text-orange-600 font-semibold"
                    }
                  >
                    {stage.societyDues.toUpperCase()}
                  </span>
                </p>
              )}
              {stage.notes && (
                <p className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded border-l-2 border-gray-200">
                  {stage.notes}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
